import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { isAdmin, ROLE_LABELS, ROLE_ORDER } from '../lib/supabase'
import { TIERS, getTierByCi } from '../lib/press'
import RankBadge from '../components/RankBadge'

export default function AdminPage() {
  const { profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [tab, setTab] = useState('pending')
  const [pendingPosts, setPendingPosts] = useState([])
  const [allPosts, setAllPosts] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [ciInputs, setCiInputs] = useState({}) // userId -> input string

  useEffect(() => {
    if (authLoading) return
    if (!profile) return
    if (!isAdmin(profile?.role)) { navigate('/'); return }
    fetchAll()
  }, [authLoading, profile?.role, tab])

  async function fetchAll() {
    setLoading(true)
    const [pending, posts, profiles] = await Promise.all([
      supabase.from('posts').select('*, profiles!posts_author_id_fkey(display_name, email)').eq('status', 'pending').order('created_at'),
      supabase.from('posts').select('*, profiles!posts_author_id_fkey(display_name)').order('created_at', { ascending: false }).limit(50),
      supabase.from('profiles').select('*').order('created_at', { ascending: false })
    ])
    setPendingPosts(pending.data || [])
    setAllPosts(posts.data || [])
    setUsers(profiles.data || [])
    setLoading(false)
  }

  async function reviewPost(id, action) {
    const status = action === 'approve' ? 'published' : 'rejected'
    let updatePayload = { status }
    if (action === 'reject') {
      const note = window.prompt('Optional: leave a note for the author:')
      if (note === null) return
      updatePayload.rejection_note = note.trim() || null
    }
    const { error } = await supabase.from('posts').update(updatePayload).eq('id', id)
    if (error) { toast(error.message, 'error'); return }
    // Remove from pending immediately
    setPendingPosts(prev => prev.filter(p => p.id !== id))
    // Update in allPosts
    setAllPosts(prev => prev.map(p => p.id === id ? { ...p, ...updatePayload } : p))
    toast(action === 'approve' ? 'Post published.' : 'Post rejected.', 'success')
  }

  async function toggleFeatured(post) {
    await supabase.from('posts').update({ featured: !post.featured }).eq('id', post.id)
    toast(post.featured ? 'Removed from featured.' : 'Set as featured piece.', 'success')
    fetchAll()
  }

  async function changeRole(userId, newRole) {
    const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', userId)
    if (error) { toast(error.message, 'error'); return }
    toast('Role updated.', 'success')
    fetchAll()
  }

  async function appointVoice(userId, appoint) {
    const { error } = await supabase
      .from('profiles')
      .update({ is_voice: appoint })
      .eq('id', userId)
    if (error) { toast(error.message, 'error'); return }
    toast(appoint ? '◆ Voice appointed.' : 'Voice status revoked.', 'success')
    fetchAll()
  }

  async function toggleFounding(userId, grant) {
    const { error } = await supabase
      .from('profiles')
      .update({ is_founding: grant })
      .eq('id', userId)
    if (error) { toast(error.message, 'error'); return }
    toast(grant ? '◆ Founding badge granted.' : 'Founding badge revoked.', 'success')
    fetchAll()
  }

  async function adjustCI(userId, amount) {
    if (!amount || amount === 0) return
    const { data: profile } = await supabase
      .from('profiles')
      .select('column_inches')
      .eq('id', userId)
      .single()
    if (!profile) return
    const newCI = Math.max(0, (profile.column_inches || 0) + amount)
    await supabase.from('profiles').update({ column_inches: newCI }).eq('id', userId)
    await supabase.from('ci_log').insert({
      user_id: userId,
      action: amount > 0 ? 'admin_award' : 'admin_deduct',
      ci: amount,
      ref_id: 'admin_manual',
    })
    toast(amount > 0 ? `+${amount} CI awarded.` : `${amount} CI deducted.`, 'success')
    fetchAll()
  }

  async function deletePost(id) {
    if (!window.confirm('Delete this post?')) return
    // Optimistically remove from both post lists
    setAllPosts(prev => prev.filter(p => p.id !== id))
    setPendingPosts(prev => prev.filter(p => p.id !== id))
    const { error } = await supabase.from('posts').delete().eq('id', id)
    if (error) { toast(error.message, 'error'); fetchAll(); return }
    toast('Post deleted.', 'success')
  }

  if (authLoading) return (
    <div className="loading-screen">
      <div className="loading-logo">The Margins<span style={{ color: 'var(--accent)' }}>.</span></div>
    </div>
  )
  if (!isAdmin(profile?.role)) return null

  const tabs = [
    { key: 'pending', label: `Pending Review ${pendingPosts.length ? `(${pendingPosts.length})` : ''}` },
    { key: 'posts', label: 'All Posts' },
    { key: 'users', label: 'Users & Roles' },
    { key: 'press', label: 'The Press' },
    { key: 'editorial', label: 'Editorial Picks' },
  ]

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <p style={styles.eyebrow}>Admin</p>
          <h1 style={styles.title}>Control Panel</h1>
        </div>
      </div>

      <div className="admin-body">
        <div className="admin-tabs">
          {tabs.map(t => (
            <button key={t.key} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }} onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <p style={styles.loadingText}>Loading...</p> : (
          <>
            {/* PENDING REVIEW */}
            {tab === 'pending' && (
              <div>
                <h2 style={styles.sectionTitle}>Posts Awaiting Review</h2>
                {pendingPosts.length === 0 ? (
                  <div className="empty-state"><p>No posts pending review.</p></div>
                ) : pendingPosts.map(p => (
                  <div key={p.id} style={styles.reviewCard}>
                    <div style={styles.reviewMeta}>
                      <span style={styles.reviewTag}>{'the feed'} · {p.tag}</span>
                      <span style={styles.reviewAuthor}>by <Link to={`/writer/${p.author_id}`} style={{ color: 'var(--ink)', textDecoration: 'underline' }}>{p.profiles?.display_name}</Link></span>
                    </div>
                    <h3 style={styles.reviewTitle}>{p.title}</h3>
                    {p.excerpt && <p style={styles.reviewExcerpt}>{p.excerpt}</p>}
                    <details style={styles.reviewDetails}>
                      <summary style={styles.reviewSummary}>Read full post</summary>
                      <div style={styles.reviewBody}>
                        {p.body.split('\n\n').map((para, i) => <p key={i} style={{ marginBottom: '1rem' }}>{para}</p>)}
                      </div>
                    </details>
                    <div style={styles.reviewActions}>
                      <button className="btn btn-sm btn-accent" onClick={() => reviewPost(p.id, 'approve')}>✓ Publish</button>
                      <button className="btn btn-sm btn-danger" onClick={() => reviewPost(p.id, 'reject')}>✕ Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ALL POSTS */}
            {tab === 'posts' && (
              <div>
                <h2 style={styles.sectionTitle}>All Posts</h2>
                <div style={styles.table}>
                  {allPosts.map(p => (
                    <div key={p.id} className="admin-table-row">
                      <div style={{ flex: 1 }}>
                        <div style={styles.tableTitle}>{p.title}</div>
                        <div style={styles.tableMeta}><Link to={`/writer/${p.author_id}`} style={{ color: 'var(--muted)' }}>{p.profiles?.display_name}</Link> · {p.section} · {new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                        <span className={`status-badge status-${p.status}`}>{p.status}</span>
                        <button
                          style={{ background: p.featured ? 'var(--accent)' : 'none', color: p.featured ? '#fff' : 'var(--muted)', border: '1px solid var(--rule)', padding: '0.3rem 0.75rem', fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', cursor: 'pointer', letterSpacing: '0.06em' }}
                          onClick={() => toggleFeatured(p)}
                          title={p.featured ? 'Remove featured' : 'Set as featured'}
                        >
                          {p.featured ? '◈ Featured' : '◇ Feature'}
                        </button>
                        {p.status === 'published' && (
                          <button className="btn btn-sm btn-ghost" onClick={() => supabase.from('posts').update({ status: 'draft' }).eq('id', p.id).then(fetchAll)}>Unpublish</button>
                        )}
                        <button className="btn btn-sm btn-danger" onClick={() => deletePost(p.id)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* USERS */}
            {tab === 'users' && (
              <div>
                <h2 style={styles.sectionTitle}>Users & Role Management</h2>

                {/* Prominent stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                  <div style={{ padding: '1.25rem 1.5rem', background: 'rgba(200,64,26,0.06)', border: '1.5px solid rgba(200,64,26,0.25)', borderRadius: 8 }}>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.4rem' }}>
                      ✦ Founding Members
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                      <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.2rem', fontWeight: 700, color: 'var(--accent)', lineHeight: 1 }}>
                        {users.filter(u => u.is_founding).length}
                      </span>
                      <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.75rem', color: 'var(--muted)' }}>/ 100</span>
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.4rem' }}>
                      {100 - users.filter(u => u.is_founding).length} spots remaining — grant via the Press tab
                    </div>
                  </div>
                  <div style={{ padding: '1.25rem 1.5rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8 }}>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '0.4rem' }}>
                      Total Writers
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.2rem', fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>
                      {users.filter(u => u.role !== 'viewer').length}
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.4rem' }}>
                      {users.length} total accounts
                    </div>
                  </div>
                  <div style={{ padding: '1.25rem 1.5rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8 }}>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '0.4rem' }}>
                      ◆ The Voice
                    </div>
                    <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.2rem', fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>
                      {users.filter(u => u.is_voice).length}
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.4rem' }}>
                      editorial appointment only — Press tab
                    </div>
                  </div>
                </div>
                <div style={styles.roleHierarchy}>
                  <p style={styles.roleHierarchyLabel}>Role Hierarchy — what each role can do</p>
                  <div style={styles.roleChain}>
                    {[
                      { role: 'viewer',      label: 'Reader',     color: 'var(--muted)',   note: 'Not signed in or legacy read-only account.' },
                      { role: 'writer_blog', label: 'Writer',     color: '#4a90d9',        note: 'All signed-up members. Can write & submit pieces.' },
                      { role: 'mod',         label: 'Moderator',  color: 'var(--warning)', note: 'Admin-assigned. Can publish, reject, delete content.' },
                      { role: 'admin',       label: 'Admin',      color: 'var(--accent)',  note: 'Full access. Manages roles, all content.' },
                    ].map(({ role, label, color, note }, i, arr) => (
                      <span key={role} style={styles.roleChainItem}>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          <span style={{ ...styles.roleChainRole, color }}>{label}</span>
                          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.5rem', color: 'var(--muted)', letterSpacing: '0.04em', maxWidth: 140 }}>{note}</span>
                        </span>
                        {i < arr.length - 1 && <span style={styles.roleChainArrow}>→</span>}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={styles.table}>
                  {users.map(u => (
                    <div key={u.id} className="admin-table-row">
                      <div style={styles.userAvatar}>{u.display_name?.[0]?.toUpperCase()}</div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.tableTitle}><Link to={`/writer/${u.id}`} style={{ color: 'var(--ink)' }}>{u.display_name}</Link></div>
                        <div style={styles.tableMeta}>{u.email}</div>
                      </div>
                      <select
                        style={styles.roleSelect}
                        value={u.role}
                        onChange={e => changeRole(u.id, e.target.value)}
                        disabled={u.email === 'shaurya.maheshwari2016@gmail.com' || u.email === 'savyaraj13@gmail.com'}
                      >
                        {['viewer', 'writer_blog', 'mod', 'admin'].map(r => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* THE PRESS */}
            {tab === 'press' && (
              <div>
                <div style={{ padding: '1.5rem', background: '#0A0908', border: '1px solid #2C2820', marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ fontFamily: "'Courier New', monospace", fontSize: '0.6rem', letterSpacing: '0.18em', color: '#C8401A', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                        THE PRESS — RANK MANAGEMENT
                      </p>
                      <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, color: '#F5F0E8', marginBottom: '0.4rem' }}>
                        Appoint Voices & Award Column Inches
                      </h2>
                      <p style={{ fontFamily: "'Courier New', monospace", fontSize: '0.65rem', color: '#7A7268', lineHeight: 1.6, maxWidth: '60ch' }}>
                        The Voice is the only rank that cannot be earned automatically. It is an editorial appointment — given to writers whose work has genuinely shaped The Margins. Use it sparingly. It means something.
                      </p>
                    </div>
                    <div style={{ flexShrink: 0, border: '2px solid #C8401A', padding: '0.75rem 1rem', textAlign: 'center' }}>
                      <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.5rem', color: '#C8401A', letterSpacing: '0.15em', marginBottom: '0.25rem' }}>THE MARGINS</div>
                      <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', color: '#C8401A', lineHeight: 1 }}>◆</div>
                      <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.5rem', color: '#C8401A', letterSpacing: '0.1em', marginTop: '0.25rem' }}>VOICE</div>
                    </div>
                  </div>
                </div>

                {/* Rank overview bar */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)', marginBottom: '2rem' }}>
                  {TIERS.map(tier => {
                    const count = users.filter(u =>
                      tier.rank === 12
                        ? u.is_voice
                        : !u.is_voice && getTierByCi(u.column_inches || 0).rank === tier.rank
                    ).length
                    return (
                      <div key={tier.rank} style={{ background: 'var(--paper)', padding: '0.75rem 1rem' }}>
                        <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.55rem', color: tier.color, letterSpacing: '0.1em', marginBottom: '0.2rem' }}>{tier.indicator}</div>
                        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.2rem', fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>{count}</div>
                        <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.15rem' }}>{tier.title}</div>
                      </div>
                    )
                  })}
                </div>

                {/* Writer rank table */}
                <h3 style={{ fontFamily: "'Courier New', monospace", fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '1rem' }}>
                  All Writers
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)' }}>
                  {users
                    .filter(u => u.role !== 'viewer')
                    .sort((a, b) => (b.column_inches || 0) - (a.column_inches || 0))
                    .map(u => {
                      const tier = getTierByCi(u.column_inches || 0, u.is_voice)
                      const nextTier = TIERS[tier.rank] // next tier (rank is 1-indexed)
                      const progress = nextTier && nextTier.ci !== Infinity
                        ? Math.min(100, Math.round(((u.column_inches || 0) - tier.ci) / (nextTier.ci - tier.ci) * 100))
                        : 100
                      return (
                        <div key={u.id} style={{ background: u.is_voice ? 'rgba(200,64,26,0.04)' : 'var(--paper)', padding: '1.25rem 1.5rem', borderLeft: u.is_voice ? '3px solid #C8401A' : '3px solid transparent' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            {/* Avatar */}
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: tier.color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', fontWeight: 700, flexShrink: 0 }}>
                              {u.display_name?.[0]?.toUpperCase()}
                            </div>
                            {/* Name + rank */}
                            <div style={{ flex: 1, minWidth: 140 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                                <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.95rem' }}><Link to={`/writer/${u.id}`} style={{ color: 'var(--ink)' }}>{u.display_name}</Link></span>
                                <RankBadge columnInches={u.column_inches || 0} isVoice={u.is_voice} size="sm" />
                                {u.is_voice && (
                                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: '0.5rem', letterSpacing: '0.12em', color: '#C8401A', border: '1px solid #C8401A', padding: '1px 5px' }}>VOICE</span>
                                )}
                              </div>
                              <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', color: 'var(--muted)' }}>{u.email}</div>
                            </div>
                            {/* CI + progress */}
                            <div style={{ minWidth: 140 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                                <span style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', color: tier.color, fontWeight: 700 }}>{u.column_inches || 0} CI</span>
                                {nextTier && nextTier.ci !== Infinity && (
                                  <span style={{ fontFamily: "'Courier New', monospace", fontSize: '0.55rem', color: 'var(--muted)' }}>{progress}% to {nextTier.title}</span>
                                )}
                              </div>
                              <div style={{ height: 4, background: 'var(--rule)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${progress}%`, background: tier.color, transition: 'width 0.6s ease' }} />
                              </div>
                            </div>
                            {/* Actions */}
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                              {/* Custom CI adjustment */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                <input
                                  type="number"
                                  placeholder="CI"
                                  value={ciInputs[u.id] || ''}
                                  onChange={e => setCiInputs(prev => ({ ...prev, [u.id]: e.target.value }))}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      const val = parseInt(ciInputs[u.id])
                                      if (!isNaN(val) && val !== 0) {
                                        adjustCI(u.id, val)
                                        setCiInputs(prev => ({ ...prev, [u.id]: '' }))
                                      }
                                    }
                                  }}
                                  style={{
                                    width: 60, fontFamily: "'Courier New', monospace",
                                    fontSize: '0.6rem', padding: '0.35rem 0.5rem',
                                    border: '1px solid var(--rule)', background: 'var(--cream)',
                                    color: 'var(--ink)',
                                    textAlign: 'center',
                                  }}
                                />
                                <button
                                  title="Award CI"
                                  onClick={() => {
                                    const val = Math.abs(parseInt(ciInputs[u.id]))
                                    if (!isNaN(val) && val > 0) {
                                      adjustCI(u.id, val)
                                      setCiInputs(prev => ({ ...prev, [u.id]: '' }))
                                    }
                                  }}
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.6rem', padding: '0.35rem 0.5rem', background: 'rgba(122,184,122,0.12)', border: '1px solid #7AB87A', color: '#7AB87A', cursor: 'pointer' }}
                                >
                                  +
                                </button>
                                <button
                                  title="Deduct CI"
                                  onClick={() => {
                                    const val = Math.abs(parseInt(ciInputs[u.id]))
                                    if (!isNaN(val) && val > 0) {
                                      adjustCI(u.id, -val)
                                      setCiInputs(prev => ({ ...prev, [u.id]: '' }))
                                    }
                                  }}
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.6rem', padding: '0.35rem 0.5rem', background: 'rgba(200,64,26,0.08)', border: '1px solid var(--accent)', color: 'var(--accent)', cursor: 'pointer' }}
                                >
                                  −
                                </button>
                              </div>
                              {/* Voice toggle */}
                              {u.is_voice ? (
                                <button
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', letterSpacing: '0.08em', padding: '0.35rem 0.75rem', background: 'rgba(200,64,26,0.1)', border: '1px solid #C8401A', color: '#C8401A', cursor: 'pointer' }}
                                  onClick={() => { if (window.confirm(`Revoke Voice status from ${u.display_name}?`)) appointVoice(u.id, false) }}
                                >
                                  ◆ Revoke Voice
                                </button>
                              ) : (
                                <button
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', letterSpacing: '0.08em', padding: '0.35rem 0.75rem', background: 'none', border: '1px solid var(--rule)', color: 'var(--muted)', cursor: 'pointer' }}
                                  onClick={() => { if (window.confirm(`Appoint ${u.display_name} as a Voice of The Margins?`)) appointVoice(u.id, true) }}
                                >
                                  ◆ Appoint Voice
                                </button>
                              )}
                              {/* Founding badge toggle */}
                              {u.is_founding ? (
                                <button
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', letterSpacing: '0.08em', padding: '0.35rem 0.75rem', background: 'rgba(200,64,26,0.06)', border: '1px solid var(--rule)', color: 'var(--muted)', cursor: 'pointer' }}
                                  onClick={() => { if (window.confirm(`Revoke founding badge from ${u.display_name}?`)) toggleFounding(u.id, false) }}
                                >
                                  ✦ Revoke Founding
                                </button>
                              ) : (
                                <button
                                  style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', letterSpacing: '0.08em', padding: '0.35rem 0.75rem', background: 'none', border: '1px solid var(--rule)', color: 'var(--muted)', cursor: 'pointer' }}
                                  onClick={() => { if (window.confirm(`Grant founding member badge to ${u.display_name}?`)) toggleFounding(u.id, true) }}
                                >
                                  ✦ Grant Founding
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })
                  }
                </div>
              </div>
            )}

          </>
        )}

        {/* EDITORIAL PICKS */}
        {tab === 'editorial' && (
          <div>
            <h2 style={styles.sectionTitle}>Editorial Picks</h2>
            <div style={{ padding: '1rem 1.25rem', background: 'rgba(200,64,26,0.05)', border: '1px solid rgba(200,64,26,0.2)', borderRadius: 8, marginBottom: '2rem' }}>
              <p style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.7 }}>
                Editorial picks are human-curated pieces with an editor's note. They appear prominently across the platform and go out as a notification to all readers.
                Use this sparingly — it means something.
              </p>
            </div>
            <div style={styles.table}>
              {allPosts.filter(p => p.status === 'published').map(p => (
                <div key={p.id} className="admin-table-row">
                  <div style={{ flex: 1 }}>
                    <div style={styles.tableTitle}>{p.title}</div>
                    <div style={styles.tableMeta}>
                      <Link to={`/writer/${p.author_id}`} style={{ color: 'var(--muted)' }}>{p.profiles?.display_name}</Link>
                      {' · '}{new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {p.is_editorial && <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 600 }}>◆ Editorial Pick</span>}
                    </div>
                  </div>
                  <button
                    style={{
                      fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', padding: '0.35rem 0.85rem',
                      background: p.is_editorial ? 'var(--accent)' : 'none',
                      color: p.is_editorial ? '#fff' : 'var(--muted)',
                      border: '1px solid var(--rule)', cursor: 'pointer', borderRadius: 5,
                    }}
                    onClick={async () => {
                      if (p.is_editorial) {
                        await supabase.from('posts').update({ is_editorial: false, editorial_note: null }).eq('id', p.id)
                        toast('Removed from editorial picks.', 'success')
                        fetchAll()
                      } else {
                        const note = window.prompt("Editor's note for this pick (shown to readers):")
                        if (note === null) return
                        await supabase.from('posts').update({ is_editorial: true, editorial_note: note.trim() || null }).eq('id', p.id)
                        // Notify all users
                        const { data: allUsers } = await supabase.from('profiles').select('id').neq('role', 'viewer')
                        if (allUsers) {
                          const notifications = allUsers.map(u => ({
                            user_id: u.id, actor_id: user.id, post_id: p.id,
                            type: 'published',
                            message: `Editorial Pick: "${p.title}" — ${note.trim() || 'this week\'s recommended read.'}`,
                          }))
                          await supabase.from('notifications').insert(notifications)
                        }
                        toast('Set as editorial pick. All writers notified.', 'success')
                        fetchAll()
                      }
                    }}
                  >
                    {p.is_editorial ? '◆ Remove Pick' : '◆ Set as Pick'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  header: { padding: '2.5rem 3rem', borderBottom: '1px solid var(--rule)', background: 'var(--ink)', color: 'var(--paper)' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: 'var(--paper)' },
  body: { padding: '2rem 3rem' },
  tabs: { display: 'flex', gap: 0, borderBottom: '1px solid var(--rule)', marginBottom: '2.5rem' },
  tab: { padding: '0.8rem 1.5rem', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: '-1px', fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', transition: 'all 0.2s', cursor: 'pointer' },
  tabActive: { color: 'var(--ink)', borderBottomColor: 'var(--ink)' },
  loadingText: { fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', color: 'var(--muted)' },
  sectionTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--rule)' },
  reviewCard: { border: '1px solid var(--rule)', padding: '2rem', marginBottom: '1.5rem', background: 'var(--cream)' },
  reviewMeta: { display: 'flex', gap: '1rem', marginBottom: '0.75rem' },
  reviewTag: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)' },
  reviewAuthor: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: 'var(--muted)' },
  reviewTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.75rem' },
  reviewExcerpt: { fontSize: '0.9rem', lineHeight: 1.65, color: 'var(--muted)', marginBottom: '1rem', fontStyle: 'italic' },
  reviewDetails: { marginBottom: '1.5rem' },
  reviewSummary: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', cursor: 'pointer', marginBottom: '1rem' },
  reviewBody: { marginTop: '1rem', padding: '1.5rem', background: 'var(--paper)', borderLeft: '2px solid var(--rule)', fontSize: '0.95rem', lineHeight: 1.8 },
  reviewActions: { display: 'flex', gap: '0.75rem' },
  table: { display: 'flex', flexDirection: 'column', gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)' },
  tableRow: { background: 'var(--paper)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' },
  tableTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.95rem', fontWeight: 700, marginBottom: '0.2rem' },
  tableMeta: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)' },
  userAvatar: { width: 36, height: 36, background: 'var(--ink)', color: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, borderRadius: '50%', flexShrink: 0 },
  roleSelect: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', padding: '0.4rem 0.75rem', border: '1px solid var(--rule)', background: 'var(--cream)', color: 'var(--ink)', cursor: 'pointer', letterSpacing: '0.04em' },
  roleHierarchy: { background: 'var(--cream)', border: '1px solid var(--rule)', padding: '1.25rem 1.5rem', marginBottom: '2rem' },
  roleHierarchyLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '0.75rem' },
  roleChain: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  roleChainItem: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  roleChainRole: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', fontWeight: 500 },
  roleChainArrow: { color: 'var(--rule)', fontSize: '0.8rem' },
}
